<template>
  <GlobalHeader theme="chalk" :slug="slug" />
  <main id="content" v-bind="$attrs">
    <PageHead :blok="blok" :date="date" :name="name" :slug="slug" />
    <GlobalSection :blok="{ theme: 'chalk' }">
      <article v-editable="blok" class="article">
        <div class="w-full max-w-[840px] mx-auto">
          <div class="mr-auto mb-8">
            <p v-if="blok.topic" class="article__eyebrow eyebrow">
              {{ blok.topic }}
            </p>
            <component
              :is="blok.heading_type"
              class="article__heading text-2"
              :class="[
                headingClass,
                `article__heading--${blok.heading_container_size}`,
              ]"
            >
              {{ name }}
            </component>
            <p v-if="blok.author" class="article__author">
              Written by {{ blok.author }}
            </p>
            <time
              v-if="formattedUpdatedDate"
              class="article__time"
              :datetime="blok.updated_date"
            >
              Updated {{ formattedUpdatedDate }}
            </time>
            <time
              v-else
              class="article__time"
              :datetime="blok.published_date || date"
            >
              {{ formattedPublishedDate }}
            </time>
          </div>
        </div>
        <Picture
          v-if="blok.hero_image && blok.hero_image.filename"
          v-editable="blok"
          class="article__hero-image max-w-[840px] mx-auto"
          :blok="makePicture(blok.hero_image)"
          loading="eager"
          :width="840"
        />
        <div class="article__body">
          <div class="article__components">
            <StoryblokComponent
              v-for="component in blok.content_components"
              :key="component._uid"
              :blok="component"
            />
          </div>
          <CTAGroup
            v-if="blok.ctas && blok.ctas.length > 0"
            :ctas="blok.ctas"
            class="article__ctas"
          />
          <ArticleShare
            :url="canonicalURL"
            :articlename="name"
            class="article__social-share"
          />
        </div>
      </article>
    </GlobalSection>
    <GlobalSection
      v-if="relatedArticleLength >= 1"
      :blok="{ theme: 'dark-gray' }"
    >
      <RelatedArticles :articles="makeCardData(articles)" />
    </GlobalSection>
    <Callout
      v-if="blok.callout_content && blok.callout_content[0]"
      :blok="blok.callout_content[0]"
    />
  </main>
  <GlobalFooter />
</template>

<script lang="ts" setup>
import { ISbStoryData } from '@storyblok/js';
import { useStoriesStore } from '@/stores/Stories';
import {
  ArticleBlokProps,
  ImageBlokProps,
  RelatedArticlesProps,
} from '@/types';

// Props
const props = defineProps<{
  blok: ArticleBlokProps;
  date: string;
  name: string;
  slug: string;
}>();

const store = useStoriesStore();
await store.fetchData();

// gets all data a single time upfront from the store
const data = store.getStoriesByType(['Article']);
// filter the data for only matching topic and not the current article
const filteredData: ISbStoryData[] = data.reduce(
  (a: ISbStoryData[], c: ISbStoryData) => {
    if (props.blok.topic === c.content.topic && props.name !== c.name) {
      return [...a, c];
    } else {
      return a;
    }
  },
  []
);
// only show 3 articles
const articles = filteredData.slice(0, 3);

const fullSlug = ref(props.slug);
const canonicalURL = useRuntimeConfig().public.siteURL + fullSlug.value;
const relatedArticleLength = ref(articles.length);

// Heading Class...using computed needed to retain reactivity due to use of <component>
const headingClass = computed(() => {
  return props.blok.heading_style !== 'default' && props.blok.heading_style;
});

const dateFormat: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

const formattedUpdatedDate = computed(() => {
  const date = props.blok.updated_date;
  return date ? new Date(date).toLocaleString('en-US', dateFormat) : null;
});

const formattedPublishedDate = computed(() => {
  const date = props.blok.published_date || props.date;
  return date ? new Date(date).toLocaleString('en-US', dateFormat) : null;
});

const makePicture = (src: ImageBlokProps) => ({
  image: src,
});

const imageDimensions = {
  height: 533,
  width: 400,
};

const makeCardData = (
  arr: ISbStoryData[]
): RelatedArticlesProps['articles'] => ({
  cards: arr.map((article) => ({
    link: [
      {
        aria_label: `Read more about ${article.name}`,
        display_name: 'Read More',
        link: {
          cached_url: article.full_slug,
        },
        style: 'tertiary',
      },
    ],
    media: [
      {
        component: 'LayeredPicture',
        image:
          article.content.preview_image &&
          article.content.preview_image?.filename !== ''
            ? {
                ...article.content.preview_image,
                ...imageDimensions,
              }
            : {
                ...article.content.hero_image,
                ...imageDimensions,
              },
      },
    ],
    text: [
      {
        eyebrow: article.content.topic,
        heading: [
          {
            heading_style: 'text-4',
            heading_text: {
              content: [
                {
                  content: [
                    {
                      text: article.name,
                      type: 'text',
                    },
                  ],
                  type: 'paragraph',
                },
              ],
              type: 'doc',
            },
            heading_type: 'h4',
          },
        ],
        text: {
          content: [
            {
              content: [
                {
                  text: article.content.summary,
                  type: 'text',
                },
              ],
              type: 'paragraph',
            },
          ],
          type: 'doc',
        },
      },
    ],
  })),
  column_count: '3',
});
</script>

<style lang="scss">
.article {
  @include container;

  display: flex;
  flex-direction: column;
  align-items: start;

  .cta {
    max-width: fit-content;
  }

  &__eyebrow {
    margin-block-end: var(--spacing-xxs);
  }

  &__heading {
    margin-block-end: var(--spacing-m);

    &--medium {
      max-width: 840px;
    }

    &--large {
      max-width: 1080px;
    }
  }

  &__author {
    display: flex;
    font-style: normal;
    font-weight: bold;
    margin-block-end: var(--spacing-xxxs);
  }

  &__time {
    color: var(--gray);
    font-weight: bold;
  }

  &__hero-image {
    margin-block-end: var(--spacing-xl);
    margin-block-start: var(--spacing-xxl);
    margin-inline: auto;

    @media all and #{$mq-m-max} {
      margin-block-end: var(--spacing-l);
      margin-block-start: var(--spacing-xl);
    }

    img {
      @media all and #{$mq-m-max} {
        aspect-ratio: 1;
        object-fit: cover;
      }
    }
  }

  &__body {
    @media all and #{$mq-m-min} {
      margin-inline: auto;
      max-width: 840px;
    }

    .blockquote {
      padding-block: unset;
      padding-inline: unset;
    }

    .picture__caption {
      display: block;
    }

    .picture {
      picture {
        img {
          margin-inline: auto;
        }
      }
    }
  }

  &__components {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-l);

    @media all and #{$mq-m-min} {
      gap: var(--spacing-xl);
    }
  }

  &__ctas {
    margin-block-start: var(--spacing-s);
  }

  &__social-share {
    margin-block-start: var(--spacing-l);

    @media all and #{$mq-m-min} {
      margin-block-start: var(--spacing-xl);
    }
  }
}
</style>
